// src/pages/BoardDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, updateDoc, deleteDoc,
  collection, addDoc, query, where, orderBy, onSnapshot,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import '../App.css';

export default function BoardDetail({ user, userData }) {
  const { postId } = useParams();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // 댓글 관련 상태
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  // 모바일 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 포스트 로드
  useEffect(() => {
    (async () => {
      const snap = await getDoc(doc(db, 'posts', postId));
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setPost(data);
        setEditTitle(data.title);
        setEditContent(data.content);
      } else {
        setPost(undefined);
      }
    })();
  }, [postId]);

  // 댓글 데이터 실시간 구독
  useEffect(() => {
    if (!postId) return;
    
    console.log('댓글 데이터 구독 시작:', postId);
    
    try {
      // 복합 인덱스 문제를 피하기 위해 orderBy 제거
      const q = query(
        collection(db, 'comments'),
        where('postId', '==', postId)
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        console.log('댓글 데이터 스냅샷:', snapshot.size);
        const commentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // 클라이언트 측에서 날짜순 정렬
        commentsData.sort((a, b) => {
          // createdAt이 null인 경우(방금 작성된 댓글)를 처리
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          
          const timeA = a.createdAt.toDate ? a.createdAt.toDate().getTime() : 0;
          const timeB = b.createdAt.toDate ? b.createdAt.toDate().getTime() : 0;
          return timeA - timeB;
        });
        
        // 익명 사용자에게 순번 부여
        const anonymousMap = {};
        let anonymousCount = 0;
        
        const processedComments = commentsData.map(comment => {
          if (comment.isAnonymous) {
            // 같은 authorId를 가진 익명 사용자에게 동일한 순번 부여
            if (!anonymousMap[comment.authorId]) {
              anonymousCount++;
              anonymousMap[comment.authorId] = anonymousCount;
            }
            
            return {
              ...comment,
              displayName: `익명${anonymousMap[comment.authorId]}`
            };
          }
          return comment;
        });
        
        setComments(processedComments);
      }, (error) => {
        console.error('댓글 불러오기 오류:', error);
        // 복합 인덱스 오류 확인
        if (error.code === 'failed-precondition') {
          console.log('Firestore 복합 인덱스 필요:', error.message);
        }
      });
      
      return () => unsubscribe();
    } catch (error) {
      console.error('댓글 구독 설정 오류:', error);
    }
  }, [postId]);

  // 삭제
  const handleDelete = async () => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      setIsSubmitting(true);
      await deleteDoc(doc(db, 'posts', postId));
      nav('/board');
    } catch (error) {
      console.error('게시글 삭제 중 오류:', error);
      alert('게시글 삭제 중 오류가 발생했습니다.');
      setIsSubmitting(false);
    }
  };

  // 저장(수정)
  const handleSave = async () => {
    if (!editTitle.trim() || !editContent.trim()) {
      alert('제목과 내용을 모두 입력하세요.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      await updateDoc(doc(db, 'posts', postId), {
        title: editTitle.trim(),
        content: editContent.trim()
      });
      setPost(prev => ({
        ...prev,
        title: editTitle.trim(),
        content: editContent.trim()
      }));
      setEditing(false);
      setIsSubmitting(false);
    } catch (error) {
      console.error('게시글 수정 중 오류:', error);
      alert('게시글 수정 중 오류가 발생했습니다.');
      setIsSubmitting(false);
    }
  };

  // 댓글 작성 처리
  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!commentText.trim()) {
      return;
    }
    
    // 사용자 정보 검증
    if (!user || !user.id) {
      alert('댓글을 작성하려면 로그인이 필요합니다.');
      return;
    }
    
    try {
      setIsSubmittingComment(true);
      
      // 디버깅을 위한 콘솔 로그 추가
      console.log('댓글 작성 시도:', { 
        userId: user?.id, 
        userObj: user,
        postId
      });
      
      const commentData = {
        postId: postId,
        text: commentText.trim(),
        authorId: user.id,
        authorName: user?.nickname || user?.displayName || user?.name || '사용자',
        isAnonymous: isAnonymous,
        createdAt: serverTimestamp()
      };
      
      console.log('저장할 댓글 데이터:', commentData);
      
      const docRef = await addDoc(collection(db, 'comments'), commentData);
      console.log('댓글 저장 성공:', docRef.id);
      
      setCommentText('');
      setIsSubmittingComment(false);
    } catch (error) {
      console.error('댓글 작성 중 오류:', error);
      alert('댓글 작성 중 오류가 발생했습니다. ' + error.message);
      setIsSubmittingComment(false);
    }
  };

  // 댓글 삭제 처리
  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('정말 이 댓글을 삭제하시겠습니까?')) return;
    
    try {
      await deleteDoc(doc(db, 'comments', commentId));
    } catch (error) {
      console.error('댓글 삭제 중 오류:', error);
      alert('댓글 삭제 중 오류가 발생했습니다.');
    }
  };

  // 이모지 아바타 (기본 스마일 아이콘)
  const emojiList = ['😀'];

  // 로딩 상태
  if (post === null) {
    return (
      <div style={{ 
        padding: isMobile ? '1rem' : '2rem',
        maxWidth: '100%',
      }}>
        <div style={{
          background: 'white',
          borderRadius: '0.75rem',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          padding: '3rem 1.5rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          border: '1px solid #f1f5f9',
        }}>
          <div style={{
            display: 'inline-block',
            animation: 'spin 1.5s linear infinite',
            marginBottom: '1rem',
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="2" x2="12" y2="6"></line>
              <line x1="12" y1="18" x2="12" y2="22"></line>
              <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
              <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
              <line x1="2" y1="12" x2="6" y2="12"></line>
              <line x1="18" y1="12" x2="22" y2="12"></line>
              <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
              <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
            </svg>
          </div>
          <p style={{
            fontSize: '1.1rem',
            fontWeight: 500,
            color: '#64748b',
            margin: 0,
          }}>게시글을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 존재하지 않는 게시글
  if (post === undefined) {
    return (
      <div style={{ 
        padding: isMobile ? '1rem' : '2rem',
        maxWidth: '100%',
      }}>
        <div style={{
          background: 'white',
          borderRadius: '0.75rem',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          padding: '3rem 1.5rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          border: '1px solid #f1f5f9',
        }}>
          <div style={{
            marginBottom: '1rem',
            color: '#94a3b8',
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>
          <p style={{
            fontSize: '1.1rem',
            fontWeight: 500,
            color: '#64748b',
            margin: '0 0 1.5rem 0',
          }}>존재하지 않는 게시글입니다.</p>
          <button 
            onClick={() => nav('/board')}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#6366f1',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontWeight: '600',
              fontSize: '0.95rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            목록으로
          </button>
        </div>
      </div>
    );
  }

  return !post ? (
    <div style={{ 
      padding: isMobile ? '1rem' : '2rem',
      maxWidth: '100%',
    }}>
      <div style={{
        background: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
        padding: '3rem 1.5rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        border: '1px solid #f1f5f9',
      }}>
        <div style={{
          display: 'inline-block',
          animation: 'spin 1.5s linear infinite',
          marginBottom: '1rem',
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="2" x2="12" y2="6"></line>
            <line x1="12" y1="18" x2="12" y2="22"></line>
            <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
            <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
            <line x1="2" y1="12" x2="6" y2="12"></line>
            <line x1="18" y1="12" x2="22" y2="12"></line>
            <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
            <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
          </svg>
        </div>
        <p style={{
          fontSize: '1.1rem',
          fontWeight: 500,
          color: '#64748b',
          margin: 0,
        }}>게시글을 불러오는 중...</p>
      </div>
    </div>
  ) : (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden', // 외부 컨테이너에서 오버플로우 숨김
      background: '#f8fafc'
    }}>
      {/* 게시글 콘텐츠 컨테이너 - 단일 스크롤 영역 */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        width: '100%',
        maxWidth: '100%',
        margin: '0 auto',
        paddingTop: '1rem',
        paddingBottom: '5rem',
        WebkitOverflowScrolling: 'touch',
        height: '100vh', // 전체 높이로 설정
      }}>
        {/* 메인 컨텐츠 래퍼 */}
        <div style={{
          maxWidth: isMobile ? '100%' : '800px',
          margin: '0 auto',
          padding: isMobile ? '0 1rem' : '0',
          width: '100%',
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
          }}>
            <h2 style={{
              margin: 0,
              fontSize: isMobile ? '1.5rem' : '2rem',
              fontWeight: 700,
              color: '#1e293b',
            }}>게시글</h2>
            <button 
              onClick={() => nav('/board')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.6rem 1rem',
                background: 'transparent',
                color: '#64748b',
                border: '1px solid #e2e8f0',
                borderRadius: '0.5rem',
                fontWeight: '500',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              목록으로
            </button>
          </div>

          {/* 게시글 내용 */}
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            padding: '1.5rem',
            marginBottom: '1.5rem',
            border: '1px solid #f1f5f9',
          }}>
            {editing ? (
              /* 수정 폼 */
              <form>
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: '#64748b',
                  }}>제목</label>
                  <input
                    value={editTitle}
                    onChange={e => setEditTitle(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #e2e8f0',
                      fontSize: '0.95rem',
                      outline: 'none',
                    }}
                  />
                </div>
                <div style={{ marginBottom: '2rem' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    color: '#64748b',
                  }}>내용</label>
                  <textarea
                    rows={isMobile ? 10 : 15}
                    value={editContent}
                    onChange={e => setEditContent(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #e2e8f0',
                      fontSize: '0.95rem',
                      outline: 'none',
                      resize: 'vertical',
                      minHeight: '150px',
                      lineHeight: '1.5',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '1rem',
                  flexWrap: 'wrap',
                }}>
                  <button
                    type="button"
                    onClick={() => setEditing(false)}
                    style={{
                      padding: isMobile ? '0.8rem' : '0.75rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #cbd5e1',
                      background: 'transparent',
                      color: '#64748b',
                      fontWeight: '500',
                      fontSize: '0.95rem',
                      cursor: 'pointer',
                      flex: isMobile ? '1 0 100%' : 'initial',
                      order: isMobile ? 2 : 1,
                      marginBottom: isMobile ? '0' : '0',
                    }}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={isSubmitting}
                    style={{
                      padding: isMobile ? '0.8rem' : '0.75rem',
                      borderRadius: '0.5rem',
                      border: 'none',
                      background: '#6366f1',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      flex: isMobile ? '1 0 100%' : 1,
                      order: isMobile ? 1 : 2,
                      marginBottom: isMobile ? '0.5rem' : '0',
                      opacity: isSubmitting ? 0.7 : 1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10" strokeOpacity="0.75" />
                        </svg>
                        저장 중...
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                          <polyline points="17 21 17 13 7 13 7 21"></polyline>
                          <polyline points="7 3 7 8 15 8"></polyline>
                        </svg>
                        저장하기
                      </>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              /* 게시글 보기 */
              <div>
                <div style={{
                  borderBottom: '1px solid #f1f5f9',
                  paddingBottom: '1.25rem',
                  marginBottom: '1.25rem',
                }}>
                  <h3 style={{
                    margin: '0 0 1rem 0',
                    fontSize: '1.3rem',
                    fontWeight: 700,
                    color: '#1e293b',
                    lineHeight: 1.4,
                  }}>{post.title}</h3>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: '1rem',
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}>
                      <div style={{
                        fontSize: '1.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '2.5rem',
                        height: '2.5rem',
                        background: 'rgba(99, 102, 241, 0.1)',
                        borderRadius: '50%',
                        flexShrink: 0,
                      }}>
                        {post.isAnonymous ? '🕵️' : emojiList[0]}
                      </div>
                      <span style={{
                        fontWeight: 500,
                        color: '#334155',
                      }}>{post.authorName}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      color: '#64748b',
                      fontSize: '0.9rem',
                    }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      {new Date(post.createdAt?.toDate()).toLocaleString()}
                    </div>
                  </div>
                </div>
                
                <div style={{
                  color: '#334155',
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  wordBreak: 'break-word',
                  whiteSpace: 'pre-wrap',
                  marginBottom: post.authorId === user.id ? '2rem' : '0',
                }}>
                  {post.content}
                </div>

                {/* 작성자 본인일 때만 수정/삭제 */}
                {post.authorId === user.id && (
                  <div style={{
                    display: 'flex',
                    justifyContent: isMobile ? 'center' : 'flex-end',
                    gap: '1rem',
                    marginTop: '2rem',
                    borderTop: '1px solid #f1f5f9',
                    paddingTop: '1.5rem',
                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                  }}>
                    <button 
                      onClick={() => setEditing(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1.25rem',
                        background: 'rgba(99, 102, 241, 0.1)',
                        color: '#6366f1',
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                        borderRadius: '0.5rem',
                        fontWeight: '600',
                        fontSize: '0.95rem',
                        cursor: 'pointer',
                        flex: isMobile ? '1 0 100%' : 'initial',
                        order: isMobile ? 1 : 1,
                        marginBottom: isMobile ? '0.5rem' : '0',
                        justifyContent: 'center',
                      }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      수정하기
                    </button>
                    <button 
                      onClick={handleDelete}
                      disabled={isSubmitting}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.75rem 1.25rem',
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        border: '1px solid rgba(239, 68, 68, 0.2)',
                        borderRadius: '0.5rem',
                        fontWeight: '600',
                        fontSize: '0.95rem',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        opacity: isSubmitting ? 0.7 : 1,
                        flex: isMobile ? '1 0 100%' : 'initial',
                        justifyContent: 'center',
                      }}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10" strokeOpacity="0.75" />
                          </svg>
                          삭제 중...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                          삭제하기
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* 댓글 섹션 */}
          {!editing && (
            <div style={{
              background: 'white',
              borderRadius: '0.75rem',
              boxShadow: '0 5px 15px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03)',
              padding: '1.5rem',
              marginBottom: isMobile ? '5rem' : '2rem',
              border: '1px solid rgba(241, 245, 249, 0.8)',
              position: 'relative',
            }}>
              {/* 상단 배경 강조 효과 */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #6366f1, #a5b4fc)',
                zIndex: 1,
              }}></div>
              
              <h3 style={{
                margin: '0.5rem 0 1.75rem 0',
                fontSize: '1.15rem',
                fontWeight: 600,
                color: '#1e293b',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                position: 'relative',
              }}>
                <div style={{
                  background: 'rgba(99, 102, 241, 0.1)',
                  padding: '0.5rem',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                댓글 <span style={{ 
                  background: '#f1f5f9', 
                  color: '#6366f1', 
                  fontSize: '0.85rem',
                  padding: '0.15rem 0.5rem',
                  borderRadius: '20px',
                  fontWeight: '500',
                }}>{comments.length}</span>
              </h3>
              
              {/* 댓글 작성 폼 */}
              <form onSubmit={handleAddComment} style={{
                marginBottom: '2rem',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                background: 'linear-gradient(180deg, rgba(241, 245, 249, 0.4) 0%, rgba(248, 250, 252, 0.2) 100%)',
                border: '1px solid #e2e8f0',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)',
              }}>
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="댓글을 작성하세요..."
                  style={{
                    width: '100%',
                    padding: '0.9rem',
                    borderRadius: '0.5rem',
                    border: '1px solid #e2e8f0',
                    fontSize: '0.95rem',
                    outline: 'none',
                    resize: 'vertical',
                    minHeight: '80px',
                    marginBottom: '1rem',
                    fontFamily: 'inherit',
                    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    background: 'white',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#a5b4fc';
                    e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1), inset 0 1px 2px rgba(0,0,0,0.05)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e2e8f0';
                    e.target.style.boxShadow = 'inset 0 1px 2px rgba(0,0,0,0.05)';
                  }}
                />
                
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: isMobile ? 'wrap' : 'nowrap',
                  gap: isMobile ? '0.5rem' : '0',
                }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9rem',
                    color: '#64748b',
                    cursor: 'pointer',
                    marginBottom: isMobile ? '0.5rem' : '0',
                    order: isMobile ? 2 : 1,
                    background: isAnonymous ? 'rgba(99, 102, 241, 0.08)' : 'transparent',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '0.375rem',
                    transition: 'background 0.2s',
                  }}>
                    <div style={{
                      position: 'relative',
                      width: '2.25rem',
                      height: '1.25rem',
                      borderRadius: '0.75rem',
                      backgroundColor: isAnonymous ? '#6366f1' : '#cbd5e1',
                      transition: 'background-color 0.2s ease',
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '0.125rem',
                        left: isAnonymous ? '1rem' : '0.125rem',
                        width: '1rem',
                        height: '1rem',
                        borderRadius: '50%',
                        backgroundColor: 'white',
                        transition: 'left 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      }}></div>
                      <input
                        type="checkbox"
                        checked={isAnonymous}
                        onChange={() => setIsAnonymous(!isAnonymous)}
                        style={{
                          opacity: 0,
                          width: '100%',
                          height: '100%',
                          position: 'absolute',
                          cursor: 'pointer',
                          zIndex: 1,
                        }}
                      />
                    </div>
                    <span style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      fontWeight: isAnonymous ? 500 : 400,
                    }}>
                      {isAnonymous && <span role="img" aria-label="anonymous" style={{ fontSize: '0.9rem' }}>🕵️</span>}
                      익명으로 댓글 작성
                    </span>
                  </label>
                  
                  <button
                    type="submit"
                    disabled={isSubmittingComment || !commentText.trim()}
                    style={{
                      padding: '0.75rem 1.25rem',
                      borderRadius: '0.5rem',
                      border: 'none',
                      background: (isSubmittingComment || !commentText.trim()) 
                        ? '#cbd5e1' 
                        : 'linear-gradient(135deg, #6366f1, #4f46e5)',
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      cursor: (isSubmittingComment || !commentText.trim()) ? 'not-allowed' : 'pointer',
                      opacity: (isSubmittingComment || !commentText.trim()) ? 0.7 : 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginLeft: 'auto',
                      order: isMobile ? 1 : 2,
                      width: isMobile ? '100%' : 'auto',
                      justifyContent: isMobile ? 'center' : 'flex-start',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      boxShadow: (isSubmittingComment || !commentText.trim())
                        ? 'none'
                        : '0 2px 4px rgba(99, 102, 241, 0.2)',
                    }}
                    onMouseOver={(e) => {
                      if (!isSubmittingComment && commentText.trim()) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 8px rgba(99, 102, 241, 0.3)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isSubmittingComment && commentText.trim()) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(99, 102, 241, 0.2)';
                      }
                    }}
                  >
                    {isSubmittingComment ? (
                      <>
                        <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10" strokeOpacity="0.75" />
                        </svg>
                        등록 중...
                      </>
                    ) : (
                      <>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="22" y1="2" x2="11" y2="13"></line>
                          <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                        댓글 등록
                      </>
                    )}
                  </button>
                </div>
              </form>
              
              {/* 댓글 목록 */}
              {comments.length > 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1.25rem',
                }}>
                  {comments.map(comment => (
                    <div key={comment.id} style={{
                      borderRadius: '0.75rem',
                      padding: '1rem 1.25rem 1.25rem',
                      position: 'relative',
                      background: comment.isAnonymous 
                        ? 'linear-gradient(120deg, rgba(241, 245, 249, 0.5) 0%, rgba(248, 250, 252, 0.8) 100%)'
                        : 'white',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.02)',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      marginBottom: '0.5rem',
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        marginBottom: '0.75rem',
                      }}>
                        <div style={{
                          fontSize: '1.25rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '2.5rem',
                          height: '2.5rem',
                          background: comment.isAnonymous 
                            ? 'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)' 
                            : 'linear-gradient(135deg, #818cf8 0%, #6366f1 100%)',
                          borderRadius: '50%',
                          flexShrink: 0,
                          color: 'white',
                          boxShadow: comment.isAnonymous 
                            ? '0 2px 4px rgba(100, 116, 139, 0.2)' 
                            : '0 2px 4px rgba(99, 102, 241, 0.2)',
                        }}>
                          {comment.isAnonymous ? '🕵️' : emojiList[0]}
                        </div>
                        
                        <div style={{ flex: 1 }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: isMobile ? '0.25rem' : '0',
                          }}>
                            <span style={{
                              fontWeight: 600,
                              fontSize: '0.95rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.4rem',
                              color: comment.isAnonymous ? '#64748b' : '#334155',
                            }}>
                              {comment.isAnonymous && (
                                <span style={{
                                  fontSize: '0.7rem',
                                  fontWeight: '500',
                                  background: '#e2e8f0',
                                  color: '#64748b',
                                  padding: '0.1rem 0.4rem',
                                  borderRadius: '20px',
                                }}>익명</span>
                              )}
                              {comment.isAnonymous ? comment.displayName : comment.authorName}
                            </span>
                            
                            <span style={{
                              color: '#94a3b8',
                              fontSize: '0.8rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.3rem',
                            }}>
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <polyline points="12 6 12 12 16 14" />
                              </svg>
                              {comment.createdAt?.toDate 
                                ? new Date(comment.createdAt.toDate()).toLocaleString() 
                                : '방금 전'}
                            </span>
                          </div>
                          
                          <p style={{
                            color: '#334155',
                            fontSize: '0.95rem',
                            lineHeight: 1.6,
                            margin: '0.8rem 0 0 0',
                            wordBreak: 'break-word',
                            fontWeight: '400',
                          }}>
                            {comment.text}
                          </p>
                        </div>
                      </div>
                      
                      {/* 댓글 작성자만 삭제 가능 */}
                      {comment.authorId === user.id && (
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          style={{
                            position: 'relative',
                            marginTop: '0.9rem',
                            marginLeft: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.4rem',
                            background: 'rgba(241, 245, 249, 0.8)',
                            border: 'none',
                            color: '#94a3b8',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            padding: '0.35rem 0.7rem',
                            borderRadius: '0.375rem',
                            transition: 'all 0.2s',
                            alignSelf: 'flex-end',
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                            e.currentTarget.style.color = '#ef4444';
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.background = 'rgba(241, 245, 249, 0.8)';
                            e.currentTarget.style.color = '#94a3b8';
                          }}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                          삭제
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem 0',
                  color: '#94a3b8',
                  background: 'rgba(241, 245, 249, 0.5)',
                  borderRadius: '0.75rem',
                  border: '1px dashed #e2e8f0',
                }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 1rem', opacity: 0.5 }}>
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <p style={{
                    margin: 0,
                    fontSize: '1rem',
                    fontWeight: 500,
                  }}>첫 번째 댓글을 작성해보세요!</p>
                  <p style={{
                    margin: '0.5rem 0 0 0',
                    fontSize: '0.85rem',
                    color: '#64748b',
                  }}>소중한 의견을 남겨주세요.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
